// Override API client (Phase 2 Day 3 of
// plan/audits/Deployment_Manifest_Resolution_Hierarchy_Audit_2026-05-28.md).
//
// Hand-rolled to mirror the conventions in lib/api.ts (request envelope,
// client-id header, error parsing) but in a separate module so the
// override surface can land without bloating the main client file.

import type {
  DecisionResolutionsResponse,
  OverrideRequest,
  OverrideResponse,
} from '@/types/overrides'

const API_BASE = '/api'

function getOrCreateClientId(): string {
  // Mirrors lib/api.ts behaviour — read/write a stable per-tab id.
  // Kept local so this module doesn't have to import private helpers.
  const KEY = 'ai_alm.client_id'
  try {
    let id = window.localStorage.getItem(KEY)
    if (!id) {
      id = `c_${Math.random().toString(36).slice(2, 10)}_${Date.now()}`
      window.localStorage.setItem(KEY, id)
    }
    return id
  } catch {
    return 'c_anonymous'
  }
}

function newRequestId(): string {
  return `r_${Math.random().toString(36).slice(2, 10)}`
}

async function jsonFetch<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      'X-Client-Id': getOrCreateClientId(),
      'X-Request-Id': newRequestId(),
      ...(init?.headers ?? {}),
    },
  })
  if (!response.ok) {
    let detail = ''
    try {
      const envelope = await response.json()
      detail =
        envelope?.error?.message ??
        envelope?.detail ??
        response.statusText ??
        `HTTP ${response.status}`
    } catch {
      detail = response.statusText || `HTTP ${response.status}`
    }
    throw new Error(detail)
  }
  if (response.status === 204) {
    return undefined as T
  }
  return (await response.json()) as T
}

/**
 * GET /api/projects/{projectId}/runs/{runId}/decision-resolutions
 *
 * Returns the Manifest Decisions table view-model used by the
 * G3.7 review surface.
 */
export function getDecisionResolutions(
  projectId: string,
  runId: number,
): Promise<DecisionResolutionsResponse> {
  return jsonFetch(
    `/projects/${projectId}/runs/${runId}/decision-resolutions`,
  )
}

/**
 * POST /api/projects/{projectId}/runs/{runId}/overrides
 *
 * Persists an override entry in architect-decisions.json and
 * re-resolves the manifest. The response includes a fresh
 * snapshot of the table so the UI can refresh in one round-trip.
 */
export function createOrReplaceOverride(
  projectId: string,
  runId: number,
  body: OverrideRequest,
): Promise<OverrideResponse> {
  return jsonFetch(
    `/projects/${projectId}/runs/${runId}/overrides`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  )
}
